import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";

export type ColorFieldProps = {
  label: string;
  /** The stored colour, or `null` for "Auto" — nothing stored. */
  value: string | null;
  /** Where the native picker opens while the value is Auto. */
  pickerStart: string;
  onChange: (next: string | null) => void;
};

/**
 * A colour that may be left unset. `null` is a real state, not a missing
 * default: an entity without colours gets a generated disc in a hue hashed
 * from its name, so the field must not invent white/black on its own. Picking
 * a colour stores it; the reset button returns to Auto.
 */
export const ColorField = ({
  label,
  value,
  pickerStart,
  onChange,
}: ColorFieldProps) => {
  const isAuto = value === null;

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" gutterBottom sx={{ display: "block" }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Box sx={{ position: "relative", flex: 1, height: 40 }}>
          <input
            type="color"
            aria-label={label}
            value={value ?? pickerStart}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%",
              height: 40,
              border: "none",
              padding: 0,
              background: "none",
              cursor: "pointer",
            }}
          />
          {isAuto && (
            // Covers the picker's placeholder swatch so it doesn't read as a
            // chosen colour. Clicks fall through to the input underneath.
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                border: 1,
                borderStyle: "dashed",
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.secondary",
                pointerEvents: "none",
              }}
            >
              <Typography variant="body2">Auto</Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Back to Auto">
          {/* span wrapper keeps the tooltip working when the button is
              disabled (MUI requirement). */}
          <span>
            <IconButton
              size="small"
              aria-label={`Reset ${label.toLowerCase()} to Auto`}
              onClick={() => onChange(null)}
              disabled={isAuto}
            >
              <FormatColorResetIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};
